import type { BookingPreferences, Ineligibility, SelectedSlot } from "./bookingFlowMachine"
import { bookingFlowMachine } from "./bookingFlowMachine"
import { deriveConsentGate } from "./consentGate"
import {
  BookedAppointment,
  ConsentType,
  ScreeningAppointmentType,
  ScreeningProgram,
  useBeginBooking,
  useBookingContext,
  useConsentStatus
} from "./hooks"
import { BookedPage } from "./pages/BookedPage"
import { ConsentPage } from "./pages/ConsentPage"
import { HealthQuestionsPage } from "./pages/HealthQuestionsPage"
import { NoAvailabilityPage } from "./pages/NoAvailabilityPage"
import { NotEligiblePage } from "./pages/NotEligiblePage"
import { SelectPreferencesPage } from "./pages/SelectPreferencesPage"
import { Box, Progress } from "@carrotfertility/carotene-core"
import { useMachine } from "@xstate/react"
import React from "react"

type BookingFlowProps = {
  // The program the member is booking into — drives the screening question set.
  program: ScreeningProgram
  // Navigation is the host's concern — these come in as callbacks rather than the
  // package importing the router.
  onExit?: () => void
  onReturnToProviderResults?: () => void
}

// Resolves the eligibility gate and booking context before starting the machine, so the init
// eligibility gate and the `alreadyConsented` guard decide on real values rather than defaults.
export function BookingFlow({ program, onExit, onReturnToProviderResults }: BookingFlowProps): JSX.Element {
  const bookingContext = useBookingContext()
  const consentStatus = useConsentStatus()
  const eligibility = useBeginBooking(program)

  if (bookingContext.isLoading || consentStatus.isLoading || eligibility.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Progress />
      </Box>
    )
  }

  // The consent gate: `alreadySignedConsent` skips the consent step when every required consent is
  // satisfied (the annual telehealth one lapses yearly, so it can be false again); `outstandingConsents`
  // is what the consent screen records. On a consent fetch failure this shows the screen and treats
  // all consents as outstanding so the member can still sign — see deriveConsentGate.
  const { alreadySignedConsent, outstandingConsents } = deriveConsentGate(consentStatus.data)
  const member = bookingContext.data?.member
  const memberName = member ? [member.firstName, member.lastName].filter(Boolean).join(" ") || undefined : undefined

  // Fail open on an eligibility fetch failure: safe because bookAppointment re-enforces eligibility
  // server-side before the write (MEM-771), so a failed lookup can't quietly become a booking.
  const eligibilityResult = eligibility.data
  const appointmentType: ScreeningAppointmentType = eligibilityResult?.appointmentType ?? "initial"
  const ineligibleReason = eligibilityResult && !eligibilityResult.eligible ? eligibilityResult.ineligibleReason : null

  return (
    // `useMachine` reads its input only at actor creation, so key the machine on the inputs that
    // define which booking this is — a program switch (X→Y→X with Y cached, so no loading remount)
    // would otherwise keep a stale actor. `alreadySignedConsent` is intentionally excluded: it's
    // read once for the consent-skip guard, and keying on it would remount mid-flow on a consent refetch.
    <BookingMachine
      key={`${program}:${appointmentType}:${ineligibleReason ?? ""}`}
      program={program}
      memberName={memberName}
      alreadySignedConsent={alreadySignedConsent}
      outstandingConsents={outstandingConsents}
      appointmentType={appointmentType}
      ineligibleReason={ineligibleReason}
      onExit={onExit}
      onReturnToProviderResults={onReturnToProviderResults}
    />
  )
}

function BookingMachine({
  program,
  memberName,
  alreadySignedConsent,
  outstandingConsents,
  appointmentType,
  ineligibleReason,
  onExit,
  onReturnToProviderResults
}: BookingFlowProps & {
  alreadySignedConsent: boolean
  outstandingConsents: ConsentType[]
  memberName?: string
  appointmentType: ScreeningAppointmentType
  ineligibleReason: string | null
}): JSX.Element | null {
  const [state, send] = useMachine(bookingFlowMachine, {
    input: { alreadySignedConsent, program, appointmentType, ineligibleReason }
  })
  const onContinue = () => send({ type: "CONTINUE" })
  const onDisqualified = (exclusion: Ineligibility) => send({ type: "DISQUALIFIED", exclusion })
  const onConfirmBooking = (preferences: BookingPreferences, slot: SelectedSlot, appointment: BookedAppointment) =>
    send({ type: "CONFIRM_BOOKING", preferences, slot, appointment })

  if (state.matches("consent"))
    return (
      <ConsentPage
        outstandingConsents={outstandingConsents}
        onContinue={onContinue}
        onReturnToProviderResults={onReturnToProviderResults}
      />
    )
  if (state.matches("healthQuestions"))
    return (
      <HealthQuestionsPage
        program={program}
        appointmentType={state.context.appointmentType}
        onContinue={onContinue}
        onDisqualified={onDisqualified}
        onReturnToProviderResults={onReturnToProviderResults}
      />
    )
  if (state.matches({ seeAvailability: "preferences" }))
    return (
      <SelectPreferencesPage
        program={program}
        onConfirmBooking={onConfirmBooking}
        onDisqualified={onDisqualified}
        onReturnToProviderResults={onReturnToProviderResults}
      />
    )
  if (state.matches("booked"))
    return (
      <BookedPage
        program={program}
        appointment={state.context.appointment}
        offeringId={state.context.preferences?.offeringId}
        memberName={memberName}
        onBackToHome={onExit}
      />
    )
  if (state.matches("notEligible")) return <NotEligiblePage exclusion={state.context.exclusion} onBackToHome={onExit} />
  if (state.matches("noAvailability")) return <NoAvailabilityPage onBackToHome={onExit} />
  return null
}
